import { useEffect, useState } from "react";
import { ArrowRight, FileSpreadsheet, FileText, Loader2, MoveRight, Undo2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg, money, telechargerExcel, telechargerPdf } from "@/lib/api";

const COLONNES = ["Réservation", "Plateforme", "Client", "Arrivée", "Départ", "Montant versé", "Ancien logement", "Nouveau logement", "Dépôt(s) Desjardins", "Déplacement annulé"];
const versExcel = (liste, annules) => ({
  colonnes: COLONNES,
  lignes: liste.map((d) => [
    { v: d.code }, { v: d.plateforme }, { v: d.client }, { v: d.debut }, { v: d.fin }, { v: d.montant, f: "money" },
    { v: d.ancien || "aucun logement" }, { v: d.nouveau || "aucun logement" },
    { v: d.depots.map((t) => `${t.date} · ${money(t.montant)}`).join(" ; ") || "—" }, { v: annules.has(d.id) ? "Oui" : "Non" },
  ]),
});

const Annuler = ({ d, annule, onAnnule, reimportId }) => {
  const [envoi, setEnvoi] = useState(false);
  if (annule) return <span className="text-xs font-medium text-amber-700" data-testid={`deplacement-${d.code}-annule`}>Annulé · remis sur {d.ancien}</span>;
  const go = async () => {
    setEnvoi(true);
    try {
      await api.post("/logements/deplacements/annuler", { id: d.id, logement: d.ancien, reimport_id: reimportId || "", attendu: d.nouveau || "" });
      toast.success(`${d.code} remis sur ${d.ancien} (le prochain « Tout réimporter » suivra de nouveau le fichier)`);
      onAnnule(d.id);
    } catch (e) { toast.error(errMsg(e)); setEnvoi(false); }
  };
  return (
    <Button size="sm" variant="outline" className="h-7 bg-white text-xs" disabled={envoi || !d.ancien} onClick={go} data-testid={`deplacement-${d.code}-annuler`}>
      {envoi ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Undo2 className="mr-1 h-3 w-3" />}Remettre
    </Button>
  );
};

const Ligne = ({ d, annule, onAnnule, reimportId }) => (
  <tr className={`border-t border-sky-100 align-top ${annule ? "opacity-60" : ""}`} data-testid={`deplacement-${d.code}`}>
    <td className="py-2 pr-3"><span className="font-mono font-semibold">{d.code}</span><span className="block text-xs text-slate-500">{d.plateforme} · {d.client || "—"}</span></td>
    <td className="py-2 pr-3 text-xs text-slate-600 whitespace-nowrap">{d.debut} → {d.fin}</td>
    <td className="py-2 pr-3 text-xs whitespace-nowrap">
      <span className="text-slate-500 line-through" data-testid={`deplacement-${d.code}-ancien`}>{d.ancien || "aucun logement"}</span>
      <ArrowRight className="mx-1 inline h-3 w-3 text-sky-600" />
      <span className="font-medium text-emerald-800" data-testid={`deplacement-${d.code}-nouveau`}>{d.nouveau || "aucun logement"}</span>
    </td>
    <td className="py-2 pr-3 text-xs text-slate-600" data-testid={`deplacement-${d.code}-depots`}>
      {d.depots.length ? d.depots.map((t, i) => <span key={i} className="block whitespace-nowrap">{t.date} · {money(t.montant)}</span>) : <span className="text-slate-400">pas encore de dépôt associé</span>}
    </td>
    <td className="py-2 text-right whitespace-nowrap"><Annuler d={d} annule={annule} onAnnule={onAnnule} reimportId={reimportId} /></td>
  </tr>
);

const Entete = ({ liste, annules, onClose, titre }) => {
  const [busy, setBusy] = useState(false);
  const exporter = async () => {
    setBusy(true);
    try { await telechargerExcel("Reservations deplacees", versExcel(liste, annules)); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mb-2 flex items-start justify-between gap-2">
      <div className="flex items-start gap-2">
        <MoveRight className="mt-0.5 h-4 w-4 shrink-0 text-sky-700" />
        <div>
          <h3 className="text-sm font-semibold text-sky-900" data-testid="deplacements-titre">{titre} · {liste.length} réservation(s) déplacée(s){annules.size ? ` · ${annules.size} annulé(s)` : ""}</h3>
          <p className="text-xs text-sky-800">{liste.length ? "Chaque réservation et son dépôt Desjardins suivent désormais le logement indiqué dans le fichier. « Remettre » la replace sur son ancien logement jusqu'au prochain réimport." : "Aucun déplacement : tout était déjà au bon logement."}</p>
        </div>
      </div>
      <div className="flex shrink-0 gap-1">
        {liste.length > 0 && (
          <Button size="sm" variant="outline" className="h-7 bg-white text-xs" disabled={busy} onClick={exporter} data-testid="deplacements-excel">
            {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <FileSpreadsheet className="mr-1 h-3 w-3" />}Excel
          </Button>
        )}
        {liste.length > 0 && (
          <Button size="sm" variant="outline" className="h-7 bg-white text-xs" disabled={busy} data-testid="deplacements-pdf"
            onClick={() => telechargerPdf("Reservations deplacees", versExcel(liste, annules)).catch((e) => toast.error(errMsg(e)))}>
            <FileText className="mr-1 h-3 w-3" />PDF
          </Button>
        )}
        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onClose} data-testid="deplacements-fermer"><X className="h-4 w-4" /></Button>
      </div>
    </div>
  );
};

const annulesDe = (r) => new Set(r.lignes.filter((x) => x.annule).map((x) => x.id));

export const DeplacementsPanel = ({ reimport, titre = "Réimport terminé", onClose, onChange, testId = "deplacements-panel" }) => {
  const liste = reimport.lignes;
  const [annules, setAnnules] = useState(() => annulesDe(reimport));
  useEffect(() => { setAnnules(annulesDe(reimport)); }, [reimport]);
  const annuler = (id) => { setAnnules(new Set([...annules, id])); onChange?.(); };
  return (
    <div className="mt-6 rounded-lg border border-sky-200 bg-sky-50/60 p-4" data-testid={testId}>
      <Entete liste={liste} annules={annules} onClose={onClose} titre={titre} />
      {liste.length > 0 && (
        <div className="max-h-96 overflow-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-sky-900/70">
              <th className="pb-1 font-medium">Réservation</th><th className="pb-1 font-medium">Séjour</th><th className="pb-1 font-medium">Ancien → nouveau logement</th><th className="pb-1 font-medium">Dépôt(s)</th><th className="pb-1" />
            </tr></thead>
            <tbody>{liste.map((d) => <Ligne key={d.id} d={d} annule={annules.has(d.id)} onAnnule={annuler} reimportId={reimport.id} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};
